import * as am5 from "@amcharts/amcharts5"
import * as am5plugins_exporting from "@amcharts/amcharts5/plugins/exporting"
import { getNumberFormat } from "./global"

// ==================== Number Formatting ====================

export interface NumberFormattingOptions {
  decimalSeparator?: string
  numberDecimal?: number
  showSpaceForThousand?: boolean
}

/**
 * Configures number formatting for the chart root
 * Handles decimal separator and number format configuration
 *
 * @param root - The amCharts 5 root
 * @param options - Number formatting configuration options
 */
export const configureNumberFormatting = (
  root: am5.Root,
  options: NumberFormattingOptions
): void => {
  const { decimalSeparator = ",", numberDecimal = 2, showSpaceForThousand = true } = options

  // Decimal separator configuration
  if (decimalSeparator === ",") {
    root.numberFormatter.set("numberFormat", "# ###,##")
  }

  // Number formatting
  root.numberFormatter.setAll({
    numberFormat: getNumberFormat(numberDecimal, showSpaceForThousand),
    numericFields: ["value"],
  })
}

// ==================== Color Set ====================

/**
 * Configures custom color set for the chart
 *
 * @param chart - The chart container (must support "colors" setting)
 * @param root - The amCharts 5 root
 * @param colorSetTheme - Array of color hex strings
 */
export const configureColorSet = (chart: any, root: am5.Root, colorSetTheme?: string[]): void => {
  if (colorSetTheme && colorSetTheme.length) {
    chart.set(
      "colors",
      am5.ColorSet.new(root, {
        colors: colorSetTheme.map((color: string) => am5.color(color)),
      })
    )
  }
}

// ==================== Couleur par valeur ====================

export interface ColorMapperRow {
  /** La valeur telle qu'elle s'affiche : libelle de legende, de part, de serie ou de categorie. */
  value: string
  color: string
}

/**
 * Couleur choisie pour une valeur donnee, ou `undefined` si aucune regle ne s'applique.
 *
 * Pourquoi ce mecanisme existe : `colorSetTheme` est une palette indexee sur l'ORDRE
 * d'apparition des donnees. « Conforme » n'est vert que parce qu'il arrive en premier — un mois
 * sans aucune fiche conforme fait donc hériter le vert à la valeur suivante, et deux widgets
 * d'une meme page peuvent donner deux couleurs au meme statut. Une regle explicite
 * valeur -> couleur fixe le sens.
 *
 * L'appelant retombe sur son propre comportement de palette quand cette fonction rend
 * `undefined` : aucun widget deja enregistre ne change de rendu tant qu'aucune regle n'est posee.
 *
 * La comparaison ignore la casse et les espaces de bord, parce que la valeur est SAISIE A LA MAIN
 * dans la configuration et non choisie dans une liste. En cas de doublon, la premiere regle gagne.
 */
export const resolveMappedColor = (
  label: unknown,
  colorMapper?: ColorMapperRow[]
): string | undefined => {
  if (!Array.isArray(colorMapper) || colorMapper.length === 0) return undefined
  if (label === null || label === undefined) return undefined

  const needle = String(label).trim().toLowerCase()
  if (!needle) return undefined

  const match = colorMapper.find(
    row => typeof row?.value === "string" && row.value.trim().toLowerCase() === needle
  )
  return match?.color || undefined
}

/**
 * Couleur d'une valeur de CATEGORIE (l'axe des abscisses en vertical).
 *
 * Se distingue de `resolveMappedColor` par un seul point : quand le widget regroupe sur PLUSIEURS
 * colonnes, `prepareChartData` concatène les valeurs (`join(" / ")`) et la categorie devient
 * « Valeur A / Valeur B ». Une regle posee sur « Valeur A » ne peut alors pas correspondre a la
 * chaine entiere. On essaie donc la chaine complete D'ABORD — une valeur peut legitimement contenir
 * une barre oblique, et il ne faut pas casser ce cas — puis chaque partie, dans l'ordre.
 */
export const resolveCategoryColor = (
  category: unknown,
  colorMapper?: ColorMapperRow[]
): string | undefined => {
  const direct = resolveMappedColor(category, colorMapper)
  if (direct) return direct

  if (typeof category !== "string" || category.indexOf("/") === -1) return undefined

  const parts = category.split("/")
  for (let i = 0; i < parts.length; i++) {
    const mapped = resolveMappedColor(parts[i], colorMapper)
    if (mapped) return mapped
  }
  return undefined
}

// ==================== Legend ====================

export interface LegendOptions {
  visibilityLegend: boolean
  legendPosition?: "top" | "bottom" | "left" | "right"
  displayTypeLegendLabels?: "truncate" | "wrap" | "default"
  fontSizeLegend?: number
  markerLegendWidth?: number
  markerLegendHeight?: number
  showValueLabels?: boolean
  legendLabelText?: string
}

/**
 * Creates and configures a legend for amCharts 5 charts
 * Handles all 4 legend positions (top, bottom, left, right)
 *
 * @param chart - The chart container to add the legend to
 * @param root - The amCharts 5 root
 * @param options - Legend configuration options
 * @returns The created legend or undefined if legend is not visible
 */
export const createLegend = (
  chart: am5.Container,
  root: am5.Root,
  options: LegendOptions
): am5.Legend | undefined => {
  const {
    visibilityLegend,
    legendPosition = "bottom",
    displayTypeLegendLabels = "default",
    fontSizeLegend = 12,
    markerLegendWidth = 20,
    markerLegendHeight = 20,
    showValueLabels = false,
    legendLabelText = "{name}",
  } = options

  if (!visibilityLegend) {
    return undefined
  }

  // Determine layout based on position
  const isHorizontal = legendPosition === "left" || legendPosition === "right"

  // Set chart layout based on legend position
  if (isHorizontal) {
    chart.set("layout", root.horizontalLayout)
  } else {
    chart.set("layout", root.verticalLayout)
  }

  // Create legend with appropriate settings based on position
  const legend = am5.Legend.new(
    root,
    isHorizontal
      ? {
          y: am5.percent(50),
          centerY: am5.percent(50),
          layout: root.verticalLayout,
        }
      : {
          x: am5.percent(50),
          centerX: am5.percent(50),
          layout: root.gridLayout,
        }
  )

  // Position the legend in the chart's child order
  if (legendPosition === "top" || legendPosition === "left") {
    chart.children.unshift(legend)
  } else {
    chart.children.push(legend)
  }

  // Configure labels
  legend.labels.template.setAll({
    maxWidth: 150,
    fontSize: fontSizeLegend,
  })

  // Configure value labels if needed
  if (showValueLabels) {
    legend.labels.template.set("text", `${legendLabelText}:`)
    legend.valueLabels.template.set("text", " {value}")
  } else {
    legend.labels.template.set("text", legendLabelText)
  }

  // Configure label display type
  if (displayTypeLegendLabels === "truncate") {
    legend.labels.template.setAll({
      oversizedBehavior: "truncate",
    })
    legend.itemContainers.template.set(
      "tooltipText",
      showValueLabels ? "{name}: {value}" : "{name}"
    )
  } else if (displayTypeLegendLabels === "wrap") {
    legend.labels.template.setAll({
      oversizedBehavior: "wrap",
    })
  }

  // Configure markers
  legend.markers.template.setAll({
    width: markerLegendWidth,
    height: markerLegendHeight,
  })

  // Disable focusable by default
  legend.itemContainers.template.set("focusable", false)

  return legend
}

/**
 * Removes any existing legends from a chart
 * Call this before setupChart to clean up legends on updates
 */
export const clearLegends = (chart: am5.Container): void => {
  chart.children.each(child => {
    if (child instanceof am5.Legend) {
      child.dispose()
    }
  })
}

export interface ExportMenuOptions {
  enableExportMenu: boolean
  filePrefix?: string
  dataSource?: any[]
  csvSeparator?: string
}

/**
 * Creates and configures an export menu for amCharts 5 charts
 * Handles export functionality with customizable options
 *
 * @param root - The amCharts 5 root
 * @param options - Export menu configuration options
 * @returns The created Exporting instance or undefined if not enabled
 */
export const createExportMenu = (
  root: am5.Root,
  options: ExportMenuOptions
): am5plugins_exporting.Exporting | undefined => {
  const { enableExportMenu, filePrefix = "chart", dataSource = [], csvSeparator = ";" } = options

  if (!enableExportMenu) {
    return undefined
  }

  const exporting = am5plugins_exporting.Exporting.new(root, {
    menu: am5plugins_exporting.ExportingMenu.new(root, {}),
    filePrefix: filePrefix,
    dataSource: dataSource,
    csvOptions: {
      separator: csvSeparator,
    },
  })

  return exporting
}
